(() => {
  if (customElements.get('oilogic-filter-drawer')) return;

/*
 * <oilogic-filter-drawer> — Oilogic Care
 * Slide-in behavior for the PLP filter drawer: animated open/close, scroll
 * lock, Escape, backdrop/close clicks and a focus trap. The mobile sticky
 * trigger is pure CSS (.main-collection__drawer). Filtering itself lives in
 * <oilogic-collection-filters>; without JS the drawer opens via native
 * <details> toggling.
 */
class OilogicFilterDrawer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.drawer = this.querySelector('[data-filter-drawer]');
    if (!this.drawer) return;
    this.panel = this.drawer.querySelector('[data-drawer-panel]');
    this.toggle = this.drawer.querySelector('summary');

    // Modal semantics only once JS provides the matching focus trap — the
    // no-JS fallback stays a plain <details> disclosure.
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');

    this.toggle.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        if (this.drawer.open) this.close();
        else this.open();
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target.closest('[data-drawer-backdrop]')) {
          this.close();
        }
        // Filters apply on change; the Show button only needs to close.
        if (event.target.closest('[data-drawer-show]')) this.close();
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.drawer.open) {
          event.stopPropagation();
          this.close();
        }
        if (event.key === 'Tab') this.trapFocus(event);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    document.body.style.overflow = '';
  }

  open() {
    if (this.drawer.open) return;
    this.drawer.open = true;
    // Double rAF so the off-screen transform paints before the slide-in.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.drawer.classList.add('filter-drawer--open'));
    });
    document.body.style.overflow = 'hidden';
    const close = this.drawer.querySelector('[data-drawer-close]');
    if (close) close.focus();
  }

  close() {
    if (!this.drawer.open) return;
    this.drawer.classList.remove('filter-drawer--open');
    document.body.style.overflow = '';

    const finish = () => {
      clearTimeout(fallback);
      this.panel.removeEventListener('transitionend', onEnd);
      this.drawer.open = false;
    };
    const onEnd = (event) => {
      if (event.target === this.panel) finish();
    };
    this.panel.addEventListener('transitionend', onEnd);
    const fallback = setTimeout(finish, 400);

    this.toggle.focus();
  }

  focusables() {
    return Array.from(
      this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  trapFocus(event) {
    if (!this.drawer.open) return;
    const focusable = this.focusables();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('oilogic-filter-drawer', OilogicFilterDrawer);
})();
